import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import api from '../config/api';

const GeneralTracking = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [trackingData, setTrackingData] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    loadTrackingData();
    // Auto refresh every 30 seconds
    const interval = setInterval(loadTrackingData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTrackingData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/general-tracking');
      setTrackingData(response.data);
    } catch (error) {
      console.error('Takip verileri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data
  const filteredData = trackingData.filter(item => {
    const matchesProject = !filterProject || 
      item.project?.toLowerCase().includes(filterProject.toLowerCase()) ||
      item.part_name?.toLowerCase().includes(filterProject.toLowerCase());
    const matchesStatus = !filterStatus || item.status === filterStatus;
    return matchesProject && matchesStatus;
  });

  // Status indicator
  const getStatusColor = (remaining) => {
    if (remaining === 0) return '#10b981'; // Green - Tamamlandı
    if (remaining < 10) return '#f59e0b'; // Orange - Az kaldı
    return '#3b82f6'; // Blue - Devam ediyor
  };

  const getStatusText = (remaining, total) => {
    if (remaining === 0) return '✅ Tamamlandı';
    const percent = ((total - remaining) / total * 100).toFixed(0);
    if (remaining < 10) return `⚡ %${percent}`;
    return `🔄 %${percent}`;
  };

  // Calculate totals
  const totalProjects = filteredData.length;
  const completedProjects = filteredData.filter(item => item.remaining === 0).length;
  const totalQuantity = filteredData.reduce((sum, item) => sum + (item.total_quantity || 0), 0);
  const shippedQuantity = filteredData.reduce((sum, item) => sum + (item.total_shipped || 0), 0);
  const remainingQuantity = totalQuantity - shippedQuantity;

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.text, margin: 0 }}>
            GENEL TAKİP
          </h1>
          <p style={{ color: COLORS.textLight, marginTop: '0.5rem' }}>
            Proje Bazlı Canlı Durum Takibi ({filteredData.length} proje-parça)
          </p>
        </div>
        <button
          onClick={loadTrackingData}
          style={{
            padding: '0.75rem 1.5rem',
            background: COLORS.primary,
            color: COLORS.white,
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <SummaryCard 
          title="Toplam Proje-Parça" 
          value={totalProjects} 
          icon="📊" 
          color="#3b82f6"
        />
        <SummaryCard 
          title="Tamamlanan" 
          value={completedProjects} 
          icon="✅" 
          color="#10b981"
        />
        <SummaryCard 
          title="Toplam Adet" 
          value={totalQuantity} 
          icon="📦" 
          color="#8b5cf6"
        />
        <SummaryCard 
          title="Sevk Edilen" 
          value={shippedQuantity} 
          icon="🚚" 
          color="#f59e0b"
        />
        <SummaryCard 
          title="Kalan Adet" 
          value={remainingQuantity} 
          icon="⏳" 
          color="#ef4444"
        />
        <SummaryCard 
          title="Tamamlanma" 
          value={`%${((shippedQuantity / totalQuantity * 100) || 0).toFixed(0)}`} 
          icon="📈" 
          color="#06b6d4"
        />
      </div>

      {/* Filters */}
      <div style={{
        background: COLORS.white,
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
            Proje / Parça Ara
          </label>
          <input
            type="text"
            placeholder="🔍 Proje veya parça adı..."
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `2px solid ${COLORS.background}`,
              borderRadius: '8px',
              fontSize: '0.95rem',
              outline: 'none'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
            Durum Filtrele
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `2px solid ${COLORS.background}`,
              borderRadius: '8px',
              fontSize: '0.95rem',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="">Tüm Durumlar</option>
            <option value="completed">Tamamlandı</option>
            <option value="in_progress">Devam Ediyor</option>
            <option value="pending">Beklemede</option>
          </select>
        </div>
      </div>

      {/* Tracking Table */}
      <div style={{
        background: COLORS.white,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: COLORS.textLight }}>Veri bulunamadı</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: COLORS.background }}>
                  <th style={tableHeaderStyle}>Proje</th>
                  <th style={tableHeaderStyle}>Parça Adı</th>
                  <th style={tableHeaderStyle}>Parça-Profil</th>
                  <th style={tableHeaderStyle}>Toplam Adet</th>
                  <th style={tableHeaderStyle}>Saha Sevk</th>
                  <th style={tableHeaderStyle}>Fabrika Galvaniz</th>
                  <th style={tableHeaderStyle}>Fabrika Siyah</th>
                  <th style={tableHeaderStyle}>Kalan Adet</th>
                  <th style={tableHeaderStyle}>Durum</th>
                  <th style={tableHeaderStyle}>Şantiye Durumu</th>
                  <th style={tableHeaderStyle}>Üretim Durumu</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => {
                  const shipped = item.saha_sevk + item.fabrika_galvaniz + item.fabrika_siyah;
                  return (
                    <tr key={index} style={{ 
                      borderBottom: `1px solid ${COLORS.background}`,
                      background: index % 2 === 0 ? COLORS.white : '#fafafa'
                    }}>
                      <td style={{...tableCellStyle, fontWeight: '600'}}>
                        {item.project}
                      </td>
                      <td style={tableCellStyle}>{item.part_name}</td>
                      <td style={tableCellStyle}>{item.part_profile || '-'}</td>
                      <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '600'}}>
                        {item.total_quantity}
                      </td>
                      <td style={{...tableCellStyle, textAlign: 'center', background: item.saha_sevk > 0 ? '#ecfdf5' : 'transparent'}}>
                        {item.saha_sevk || '-'}
                      </td>
                      <td style={{...tableCellStyle, textAlign: 'center', background: item.fabrika_galvaniz > 0 ? '#fef3c7' : 'transparent'}}>
                        {item.fabrika_galvaniz || '-'}
                      </td>
                      <td style={{...tableCellStyle, textAlign: 'center', background: item.fabrika_siyah > 0 ? '#f3f4f6' : 'transparent'}}>
                        {item.fabrika_siyah || '-'}
                      </td>
                      <td style={{
                        ...tableCellStyle,
                        textAlign: 'center',
                        fontWeight: '700',
                        color: item.remaining === 0 ? '#10b981' : item.remaining < 10 ? '#f59e0b' : COLORS.text
                      }}>
                        {item.remaining}
                      </td>
                      <td style={{...tableCellStyle, textAlign: 'center'}}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          background: getStatusColor(item.remaining),
                          color: COLORS.white,
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {getStatusText(item.remaining, item.total_quantity)}
                        </span>
                      </td>
                      <td style={tableCellStyle}>{item.site_status || '-'}</td>
                      <td style={tableCellStyle}>{item.production_status || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, icon, color }) => (
  <div style={{
    background: COLORS.white,
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    textAlign: 'center',
    borderTop: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{icon}</div>
    <h3 style={{ fontSize: '0.95rem', color: COLORS.textLight, margin: 0, marginBottom: '0.5rem' }}>
      {title}
    </h3>
    <p style={{ fontSize: '2rem', fontWeight: '700', color, margin: 0 }}>
      {value}
    </p>
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  color: COLORS.text
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.95rem',
  color: COLORS.text
};

export default GeneralTracking;
